/**
 * Loads the dressing agent on demand and memoizes the result so every form on
 * the page shares one download.
 *
 * The agent is served from our own subdomain, which proxies the upstream CDN so
 * ad blockers don't strip it. The upstream default endpoint is kept as a
 * fallback for the identification call itself.
 *
 * Nothing here runs on its own. The download starts only when primeDressing()
 * is called from a form trigger — the login page, the login modal opening, the
 * join modal opening, the review page — which mirrors how reCAPTCHA's script is
 * held back until one of those same moments. A guest page with no form on it
 * must not reach the endpoint at all.
 *
 * The agent instance is held here at module level and never inside Alpine data:
 * Alpine's reactive Proxy breaks third-party class instances. See
 * resources/js/alpine/README-data-patterns.md, which documents the same hazard
 * for grecaptcha.
 */

let agentPromise = null;

/**
 * Bounds the agent download itself. The script can stall behind a blocked or
 * slow CDN without ever rejecting on its own. Generous because a prime that is
 * still in flight costs a waiting user nothing — only the two deadlines below
 * sit on the submit path.
 */
const LOAD_TIMEOUT_MS = 10000;

/**
 * How long a submit will wait for the agent to be ready before giving up on a
 * collection. Priming happens when the form is first shown, so by submit time
 * the download has almost always finished and this resolves instantly. It is
 * the cold-submit fallback: a submit that lands before the prime completes, or
 * one on a page where no prime ever fired.
 */
const AGENT_READY_TIMEOUT_MS = 2000;

/**
 * Bounds the identification call once the agent is ready. Kept separate from the
 * wait above so a slow download cannot eat the budget the call needs — the bug
 * a single combined deadline had once priming moved off page load.
 *
 * Worst case in front of a submit is therefore AGENT_READY_TIMEOUT_MS +
 * IDENTIFY_TIMEOUT_MS. That ceiling is only reachable on a cold submit; a primed
 * one pays the identification call alone.
 */
const IDENTIFY_TIMEOUT_MS = 3000;

/**
 * Races a promise against a deadline, clearing the timer so a win doesn't leave
 * one pending.
 *
 * @template T
 * @param {Promise<T>} promise
 * @param {number} ms
 * @param {string} message
 * @returns {Promise<T>}
 */
function withDeadline(promise, ms, message) {
    let timer;

    const deadline = new Promise((resolve, reject) => {
        timer = setTimeout(() => reject(new Error(message)), ms);
    });

    return Promise.race([promise, deadline]).finally(() => clearTimeout(timer));
}

function loadAgent({ apiKey, endpoint }) {
    if (agentPromise) {
        return agentPromise;
    }

    const promise = withDeadline(
        import(/* @vite-ignore */ `${endpoint}/web/v3/${apiKey}`).then((module) => module.load({ endpoint: [endpoint, module.defaultEndpoint] })),
        LOAD_TIMEOUT_MS,
        'Timed out loading the dressing agent.',
    ).catch((error) => {
        // Clear the memo only while it still points at this attempt, so a late
        // failure from a superseded attempt cannot null an in-flight promise.
        if (agentPromise === promise) {
            agentPromise = null;
        }

        throw error;
    });

    agentPromise = promise;

    return agentPromise;
}

/**
 * Starts the agent download without waiting for it. Safe to call repeatedly —
 * the memo above makes every call after the first a no-op.
 *
 * Called when a form that needs dressing is first shown, so the download
 * overlaps with the user filling it in, leaving only the identification call on
 * the submit path.
 */
export function primeDressing(config) {
    if (!config.apiKey) {
        return;
    }

    loadAgent(config).catch(() => {
        // Surfaced by collectDressing() if a submit actually needs it.
    });
}

/**
 * Resolves to the identification result as a JSON string, or '' when nothing
 * could be collected.
 *
 * Waits a bounded time for the agent to be ready and only then makes the
 * identification call, so an unprimed or still-downloading agent degrades to an
 * empty value instead of a hung submit.
 *
 * This never rejects. Every caller is fail-open by design: an ad blocker, a
 * blocked CDN, a missing API key, or a slow agent must not stop a login, review,
 * or signup from going through.
 *
 * @param {{apiKey: string, endpoint: string}} config
 * @param {{tag?: string}} options
 * @returns {Promise<string>}
 */
export async function collectDressing(config, { tag } = {}) {
    if (!config.apiKey) {
        // Fail-open means a missing key degrades silently, so warn loudly enough
        // to be visible rather than letting collection quietly never happen.
        console.warn('Dressing public API key is not configured; skipping collection.');

        return '';
    }

    try {
        // loadAgent() returns the primed promise when one is in flight, and starts
        // the download when no prime fired for this page.
        const agent = await withDeadline(loadAgent(config), AGENT_READY_TIMEOUT_MS, 'Timed out waiting for the dressing agent.');

        const result = await withDeadline(agent.get({ extendedResult: true, products: ['identification', 'botd'], tag }), IDENTIFY_TIMEOUT_MS, 'Timed out collecting dressing.');

        return JSON.stringify(result);
    } catch (error) {
        console.warn('Dressing collection failed; continuing without one.', error);

        return '';
    }
}
